import Phaser from 'phaser';
import { createRoot } from 'react-dom/client';
import { Runtime, loadPack, EngineTooOldError, DESIGN_WIDTH, DESIGN_HEIGHT, type PackSource } from '@hon/core';
import type { Catalog } from '@hon/schema';
import { StageScene } from './scenes/StageScene.js';
import { App } from './ui/App.js';
import { installScanlines } from './ui/crt.js';
import { apply as applyPrefs, load as loadPrefs } from './ui/prefs.js';
import './style.css';
import './title.css';
import './library.css';
import './settings.css';
import './profile.css';
import './shop.css';

/**
 * Pack origin. Local `/packs/kitchen` in dev; in production first-party packs
 * come from R2 via the Workers API, which is why this is a variable and not a
 * constant. `packs/base` is still built and tested — it is the scaffold the
 * runtime smoke tests run against, not something a player ever loads.
 */
const source: PackSource = {
  baseUrl: import.meta.env['VITE_PACK_URL'] ?? '/packs/kitchen',
};

/**
 * Phaser 4 is WebGL-only — there is no Canvas renderer to fall back to. On a
 * GPU-blocklisted Android WebView, or Electron launched with --disable-gpu, the
 * stage would simply be blank behind a working UI. Detect it up front so the
 * stage can degrade to CSS backgrounds instead of silently disappearing.
 */
function hasWebGL(): boolean {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') ?? c.getContext('webgl'));
  } catch {
    return false;
  }
}

/**
 * The real-error surface.
 *
 * Deliberately OUTSIDE the horror vocabulary: no CRT, no scanlines, no red, no
 * glitch. Because this game's art direction *is* simulated malfunction, a genuine
 * failure has to look unmistakably different or neither the player nor a store
 * reviewer can tell a crash from a scripted one. Russian, with a copyable code
 * and a way out.
 */
function fatal(area: string, err: unknown): void {
  console.error(`[hon:${area}]`, err);
  const code = `HON-${area.toUpperCase()}-${Math.abs(hashCode(String(err))) % 1000}`;
  document.documentElement.dataset['effects'] = 'off';
  document.body.innerHTML = '';

  /*
   * An out-of-date app is not a failure, and telling the player to check their
   * connection and retry is advice that cannot possibly work — the pack will be
   * too new on every attempt. The loader raises a distinct error for this
   * precisely so the player gets an instruction that resolves it.
   */
  const stale = err instanceof EngineTooOldError;

  const wrap = document.createElement('div');
  wrap.className = 'fatal';
  wrap.setAttribute('role', 'alert');
  wrap.innerHTML = stale
    ? `
    <h1>Нужно обновить приложение</h1>
    <p>Эта глава сделана для более новой версии игры. Обновите приложение, чтобы продолжить.</p>
    <p>Если обновление страницы не помогло, установите свежую версию из магазина.</p>
    <p class="fatal-code">Требуется <code>${escapeHtml(err.required)}</code>, установлена <code>${escapeHtml(err.running)}</code></p>
    <div class="fatal-actions">
      <button id="hon-retry">Обновить</button>
    </div>
  `
    : `
    <h1>Не удалось запустить игру</h1>
    <p>Похоже, что-то пошло не так при загрузке. Проверьте подключение к сети и попробуйте ещё раз.</p>
    <p class="fatal-code">Код ошибки: <code>${code}</code></p>
    <div class="fatal-actions">
      <button id="hon-retry">Повторить</button>
    </div>
  `;
  document.body.appendChild(wrap);
  document.getElementById('hon-retry')?.addEventListener('click', () => location.reload());
}

/** Version strings come from the pack, which is data — never interpolate it raw. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return h;
}

async function boot(): Promise<void> {
  applyPrefs(loadPrefs());
  installScanlines();

  const [pack, catalog] = await Promise.all([
    loadPack(source),
    fetch('catalog.json').then((r) => {
      if (!r.ok) throw new Error(`catalog ${r.status}`);
      return r.json() as Promise<Catalog>;
    }),
  ]);

  const runtime = new Runtime(pack, { now: Date.now() });

  if (hasWebGL()) {
    new Phaser.Game({
      type: Phaser.WEBGL,
      parent: 'stage',
      backgroundColor: '#05050a',
      /*
       * ENVELOP, not FIT. Portrait mobile-first, and the stage must reach every
       * edge: FIT letterboxed 77-91px of black onto every modern phone, and —
       * worse — left the DOM dialogue box sitting at a different fraction of the
       * stage on every device, so art briefs written as "% of the canvas" were
       * wrong by 5-6 points on the most common hardware.
       *
       * ENVELOP fills the viewport and crops the overflow instead. What may be
       * cropped is bounded and documented in @hon/core's layout module.
       */
      scale: {
        mode: Phaser.Scale.ENVELOP,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width: DESIGN_WIDTH,
        height: DESIGN_HEIGHT,
      },
      scene: [new StageScene(runtime, pack, source)],
    });
  } else {
    // Degraded mode is shippable: the UI, story, saves and audio all still work.
    document.documentElement.dataset['degraded'] = 'true';
    console.warn('[hon] no WebGL — running in degraded mode, stage effects disabled');
  }

  const mount = document.getElementById('ui');
  if (!mount) throw new Error('#ui mount missing from index.html');
  createRoot(mount).render(<App runtime={runtime} pack={pack} baseUrl={source.baseUrl} catalog={catalog} />);
}

boot().catch((err: unknown) => fatal('boot', err));
